import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, MoreVertical } from 'lucide-react';
import { useIndustry } from '../context/IndustryContext';
import { getIndustryData } from '../data/industryDataService';
import Walkthrough from '../components/Walkthrough';

const walkthroughSteps = [
  { target: '[data-testid="dashboard-header"]', title: 'Welcome to CRO View, Risk Radar', description: 'You\'re now viewing Forsyt as a CRO / Revenue Leader. Risk Radar surfaces every deal that needs attention right now, stalled pipeline, at-risk commits, and flagged renewals. No digging through dashboards. Use "Switch to Rep View" to see what your reps see daily.' },
  { target: '[data-testid="risk-metrics"]', title: 'Risk Metrics', description: 'Three clickable cards showing at-risk pipeline. "Upside at risk" = uncommitted deals that may slip. "Commit at risk" = committed deals in danger. "Deals at risk" = pipeline and renewal accounts flagged high-priority. Click any card to filter the table below.' },
  { target: '[data-testid="deals-pipeline-columns"]', title: 'Company Name', description: 'Shows the account name and the assigned rep. Quickly identify which deals belong to which team member.' },
  { target: '[data-testid="deals-pipeline-columns"]', title: 'Status', description: 'The derived risk type for the deal, e.g. Ghosting, Competitor Eval, Budget Hold. Tells you exactly why this deal is flagged.' },
  { target: '[data-testid="deals-pipeline-columns"]', title: 'Actions', description: 'Number of pending tasks tied to this deal. A higher count means more outstanding follow-ups that need attention.' },
  { target: '[data-testid="deals-pipeline-columns"]', title: 'Due In', description: 'How long the deal has been stuck or how many days until the next milestone. Helps prioritize which deals need immediate action.' },
  { target: '[data-testid="deals-pipeline-columns"]', title: 'Deal Value', description: 'The annual recurring revenue (ARR) attached to this deal. Gives you instant context on the financial impact at risk.' },
  { target: '[data-testid="deal-row-1"]', title: 'Deal Details', description: 'Click any row to open the full account view with deal summary, pending actions, activity timeline, and key contacts.' },
];

const filterKeys = ['upside', 'commit', 'deals'];

const MetricCard = ({ label, value, subtitle, colorScheme, isActive, onClick }) => {
  const schemes = {
    blue: {
      bg: 'bg-gradient-to-r from-blue-50 to-cyan-50',
      activeBg: 'bg-gradient-to-r from-blue-100 to-cyan-100',
      wave: 'text-blue-100',
      ring: 'ring-blue-400',
    },
    teal: {
      bg: 'bg-gradient-to-r from-emerald-50 to-teal-50',
      activeBg: 'bg-gradient-to-r from-emerald-100 to-teal-100',
      wave: 'text-emerald-100',
      ring: 'ring-emerald-400',
    },
    yellow: {
      bg: 'bg-gradient-to-r from-yellow-50 to-amber-50',
      activeBg: 'bg-gradient-to-r from-yellow-100 to-amber-100',
      wave: 'text-yellow-100',
      ring: 'ring-yellow-400',
    },
  };
  const scheme = schemes[colorScheme] || schemes.blue;

  return (
    <button
      onClick={onClick}
      className={`relative ${isActive ? scheme.activeBg : scheme.bg} rounded-xl p-6 overflow-hidden min-h-[140px] flex flex-col justify-between text-left w-full transition-all duration-200 cursor-pointer ${isActive ? `ring-2 ${scheme.ring} shadow-md` : 'hover:shadow-sm'}`}
      data-testid={`metric-card-${colorScheme}`}
    >
      {/* Wave decoration */}
      <svg className={`absolute right-0 bottom-0 w-40 h-24 ${scheme.wave}`} viewBox="0 0 160 96" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 96L10 80C20 64 40 32 60 26.7C80 21.3 100 42.7 120 48C140 53.3 160 42.7 160 37.3V96H0Z" opacity="0.5" />
        <path d="M0 96L10 85.3C20 74.7 40 53.3 60 48C80 42.7 100 53.3 120 58.7C140 64 160 64 160 64V96H0Z" opacity="0.3" />
      </svg>
      <div className="relative z-10">
        <p className="text-sm font-medium text-gray-700 mb-1">{label}</p>
        <p className="text-3xl font-bold text-gray-900">{value >= 1000000 ? `$${(value / 1000000).toFixed(1)}M` : `$${value.toLocaleString()}`}</p>
      </div>
      <p className="text-xs text-gray-500 relative z-10 mt-2">{subtitle}</p>
    </button>
  );
};

const StatusPill = ({ status, statusColor }) => (
  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusColor}`}>
    {status}
  </span>
);

export default function RiskRadar() {
  const [activeFilter, setActiveFilter] = useState(null);
  const navigate = useNavigate();
  const { selectedIndustry } = useIndustry();
  const { riskMetrics, dealsPipeline } = getIndustryData(selectedIndustry);

  const handleCardClick = (filterKey) => {
    setActiveFilter(prev => prev === filterKey ? null : filterKey);
  };

  const filteredDeals = activeFilter
    ? dealsPipeline.filter(deal => deal.riskCategory === activeFilter)
    : dealsPipeline;

  return (
    <div className="space-y-6" data-testid="risk-radar-page">
      <Walkthrough steps={walkthroughSteps} pageKey="risk-radar" />
      {/* Metric Cards */}
      <div className="grid grid-cols-3 gap-5" data-testid="risk-metrics">
        <MetricCard
          label={riskMetrics.upsideAtRisk.label}
          value={riskMetrics.upsideAtRisk.value}
          subtitle={riskMetrics.upsideAtRisk.subtitle}
          colorScheme={riskMetrics.upsideAtRisk.color}
          isActive={activeFilter === 'upside'}
          onClick={() => handleCardClick('upside')}
        />
        <MetricCard
          label={riskMetrics.commitAtRisk.label}
          value={riskMetrics.commitAtRisk.value}
          subtitle={riskMetrics.commitAtRisk.subtitle}
          colorScheme={riskMetrics.commitAtRisk.color}
          isActive={activeFilter === 'commit'}
          onClick={() => handleCardClick('commit')}
        />
        <MetricCard
          label={riskMetrics.dealsAtRisk.label}
          value={riskMetrics.dealsAtRisk.value}
          subtitle={riskMetrics.dealsAtRisk.subtitle}
          colorScheme={riskMetrics.dealsAtRisk.color}
          isActive={activeFilter === 'deals'}
          onClick={() => handleCardClick('deals')}
        />
      </div>

      {/* Deals Pipeline */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm" data-testid="deals-pipeline">
        <div className="px-6 py-5 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Deals pipeline</h2>
          {activeFilter && (
            <button
              onClick={() => setActiveFilter(null)}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
              data-testid="clear-filter-btn"
            >
              Clear filter
            </button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead data-testid="deals-pipeline-columns">
              <tr className="border-t border-gray-100">
                <th className="text-left text-xs font-medium text-gray-500 px-6 py-3 w-[30%]">Company Name</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Status</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Actions</th>
                <th className="text-left text-xs font-medium text-gray-500 px-4 py-3">Due In</th>
                <th className="text-right text-xs font-medium text-gray-500 px-4 py-3">Deal Value</th>
                <th className="w-10 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {filteredDeals.map((deal) => (
                <tr key={deal.id} onClick={() => navigate(`/dashboard/opportunities/${deal.opportunityId}`)} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors cursor-pointer" data-testid={`deal-row-${deal.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg ${deal.avatarColor} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                        {deal.avatar}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{deal.companyName}</p>
                        <p className="text-xs text-gray-500">{deal.contactName}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <StatusPill status={deal.status} statusColor={deal.statusColor} />
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-sm text-gray-900">{deal.actions}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-1.5 text-sm text-gray-600">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      <span>{deal.dueIn}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-bold text-gray-900">{deal.dealValue >= 1000000 ? `$${(deal.dealValue / 1000000).toFixed(1)}M` : `$${Math.round(deal.dealValue / 1000)}K`}</span>
                  </td>
                  <td className="px-2 py-4">
                    <button className="text-gray-400 hover:text-gray-600" data-testid={`deal-menu-${deal.id}`}>
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {filteredDeals.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-400">
                    No deals found for this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
